import { useCallback, useEffect, useState } from 'react';
import type { VerificationQueueItem, VerificationType } from '@mvcp/contracts';
import {
  downloadVerificationEvidence,
  listVerificationQueue,
  rejectDeclaration,
  verifyDeclaration,
} from '../api';
import { MAX_REASON_LEN } from '../lib/field-limits';
import { ConfirmModal } from './ConfirmModal';
import { Skeleton } from './Skeleton';

/**
 * Provenance ("assurance") marker labels (Story 5.2/5.4), orthogonal to any status.
 * Re-inlined here because the web consumes `@mvcp/contracts` as TYPES ONLY (a
 * runtime-value import breaks `vite build`, Story 1.6). Honest wording: `self_declared`
 * is NEVER presented as equivalent to a third-party certification.
 */
const PROVENANCE_LABEL: Record<VerificationType, string> = {
  third_party: 'Third-party verified',
  self_declared: 'Self-declared',
};

/** The date portion of an ISO timestamp (`2027-01-31`), or an em-dash when absent. */
function shortDate(iso?: string): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toISOString().slice(0, 10);
}

type ViewState =
  | { kind: 'loading' }
  | { kind: 'ready'; items: VerificationQueueItem[] }
  | { kind: 'error'; message: string };

/**
 * System-Administrator verification queue (Story 5.4). Lists the "Declared · pending
 * review" declarations produced by Story 5.2; per row the admin can review evidence,
 * Verify (promote into the one shared backbone record — propagates to every Company by
 * shared-read), or Reject/hold with a required reason. Mirrors {@link VendorPortalOverview}'s
 * loading/error/Retry contract. NO customer identity is rendered (the payload carries
 * none); the Vendor's OWN name IS shown (the admin curates the directory).
 */
export function VerificationQueueView(): React.JSX.Element {
  const [state, setState] = useState<ViewState>({ kind: 'loading' });
  // The item whose reject-with-reason modal is open (null when closed).
  const [rejecting, setRejecting] = useState<VerificationQueueItem | null>(null);
  const [reason, setReason] = useState('');
  const [rejectError, setRejectError] = useState<string | null>(null);
  const [rejectBusy, setRejectBusy] = useState(false);
  // The id currently being verified (disables its row action to block double-fire).
  const [verifyingId, setVerifyingId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const items = await listVerificationQueue();
      setState({ kind: 'ready', items });
    } catch (err: unknown) {
      setState({ kind: 'error', message: err instanceof Error ? err.message : 'Failed to load' });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function onVerify(id: string): Promise<void> {
    setRowError(null);
    setVerifyingId(id);
    try {
      await verifyDeclaration(id);
      await load();
    } catch (err: unknown) {
      setRowError(err instanceof Error ? err.message : "Couldn't verify. Try again.");
    } finally {
      setVerifyingId(null);
    }
  }

  function openReject(item: VerificationQueueItem): void {
    setRejecting(item);
    setReason('');
    setRejectError(null);
  }

  async function onConfirmReject(): Promise<void> {
    if (!rejecting) return;
    const trimmed = reason.trim();
    if (trimmed.length === 0) {
      // Story 6.10: the reason is always RECORDED; it is only emailed for a vendor's own
      // declaration (a manager submission sends no vendor notice — see the modal copy).
      setRejectError(
        rejecting.submitterCompanyName
          ? 'A reason is required — it is recorded on the rejection.'
          : 'A reason is required — the Vendor is emailed this reason.',
      );
      return;
    }
    setRejectError(null);
    setRejectBusy(true);
    try {
      await rejectDeclaration(rejecting.id, trimmed);
      setRejecting(null);
      await load();
    } catch (err: unknown) {
      setRejectError(err instanceof Error ? err.message : "Couldn't reject. Try again.");
    } finally {
      setRejectBusy(false);
    }
  }

  async function onDownloadEvidence(itemId: string, eid: string): Promise<void> {
    setRowError(null);
    // Open the tab SYNCHRONOUSLY inside the click gesture, then navigate it once the
    // authenticated blob resolves. Calling window.open AFTER the await loses the
    // user-activation, so browsers block it as a programmatic popup and "Review
    // evidence" silently does nothing. (No `noopener` here: we need the handle to
    // navigate, and the blob is same-origin + admin-only.)
    const tab = window.open('', '_blank');
    try {
      const blob = await downloadVerificationEvidence(itemId, eid);
      const url = URL.createObjectURL(blob);
      if (tab) {
        tab.location.href = url;
        // Release the object URL after the browser has had time to open it.
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } else {
        // Popups fully blocked — surface it rather than failing silently.
        URL.revokeObjectURL(url);
        setRowError('Allow pop-ups for this site to review evidence.');
      }
    } catch (err: unknown) {
      tab?.close();
      setRowError(err instanceof Error ? err.message : "Couldn't open the evidence.");
    }
  }

  return (
    <>
      <div>
        <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>
          Verification queue
        </h1>
        <p className="muted" style={{ margin: 0 }}>
          Review declared certificates. Verifying promotes the certificate to every company that
          uses the vendor; rejecting records the reason, and emails it to the vendor when the
          vendor declared it themselves.
        </p>
      </div>

      {state.kind === 'loading' && (
        <section className="card stack" aria-busy="true" aria-label="Loading verification queue">
          <Skeleton height={20} width="40%" />
          <Skeleton height={64} />
          <Skeleton height={64} />
        </section>
      )}

      {state.kind === 'error' && (
        <section className="card stack" role="alert">
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Couldn’t load the queue</h2>
          <p className="muted" style={{ margin: 0 }}>{state.message}</p>
          <button
            type="button"
            className="btn-accent"
            data-surface="accent"
            onClick={() => void load()}
            style={{ alignSelf: 'flex-start' }}
          >
            Retry
          </button>
        </section>
      )}

      {state.kind === 'ready' && (
        <section className="card stack" aria-labelledby="queue-heading">
          <h2 id="queue-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
            Pending declarations
          </h2>

          {rowError && (
            <div role="alert" aria-live="assertive">
              <span className="status-pill" data-tone="bad">
                <span className="status-pill__dot" aria-hidden="true" />
                {rowError}
              </span>
            </div>
          )}

          {state.items.length === 0 ? (
            <p className="muted" style={{ margin: 0 }}>Nothing is waiting for review.</p>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {state.items.map((item) => (
                <li
                  key={item.id}
                  className="stack"
                  data-queue-item={item.id}
                  style={{ gap: 6, borderTop: '1px solid var(--border)', paddingTop: 10 }}
                >
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700 }} data-numeric>
                      {item.framework.code}
                    </span>
                    <span style={{ fontWeight: 600 }}>{item.framework.name}</span>
                    <span
                      className="status-pill"
                      data-tone="neutral"
                      data-provenance={item.framework.verificationType}
                      aria-label={`Provenance: ${PROVENANCE_LABEL[item.framework.verificationType]}`}
                    >
                      {PROVENANCE_LABEL[item.framework.verificationType]}
                    </span>
                  </div>
                  <div className="muted" style={{ fontSize: 13, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    <span>
                      Vendor: <span style={{ fontWeight: 600 }}>{item.vendorName}</span>
                    </span>
                    <span>
                      Subject: {item.subjectType === 'device' ? (item.deviceLabel ?? 'Device') : 'Company'}
                    </span>
                    {/*
                      Provenance (Story 6.10): a set `submitterCompanyName` means a
                      customer's Compliance Manager submitted third-party evidence ABOUT
                      this Vendor; absent means the Vendor declared it itself (Story 5.2).
                      The distinction drives what verifying does — a vendor declaration
                      also promotes the Vendor's global verification status, a manager
                      submission promotes only the certificate (D2) — so the admin must
                      be able to SEE it, not just receive it in the payload.
                    */}
                    <span data-submitter={item.submitterCompanyName ?? ''}>
                      Submitted by:{' '}
                      <span style={{ fontWeight: 600 }}>
                        {item.submitterCompanyName ?? `${item.vendorName} (vendor)`}
                      </span>
                    </span>
                    <span>Authority: {item.approvingAuthority ?? '—'}</span>
                    <span>Expires: {shortDate(item.expiresAt)}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    {item.evidence.length === 0 ? (
                      <span className="muted" style={{ fontSize: 13 }}>No evidence attached</span>
                    ) : (
                      item.evidence.map((ev) => (
                        <button
                          key={ev.id}
                          type="button"
                          className="icon-button"
                          onClick={() => void onDownloadEvidence(item.id, ev.id)}
                          aria-label={`Review evidence (${ev.contentType})`}
                        >
                          Review evidence
                        </button>
                      ))
                    )}
                    <button
                      type="button"
                      className="btn-accent"
                      data-surface="accent"
                      onClick={() => void onVerify(item.id)}
                      disabled={verifyingId === item.id}
                      style={{ marginLeft: 'auto' }}
                    >
                      {verifyingId === item.id ? 'Verifying…' : 'Verify'}
                    </button>
                    <button
                      type="button"
                      className="icon-button"
                      data-tone="bad"
                      onClick={() => openReject(item)}
                    >
                      Reject
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {rejecting && (
        <ConfirmModal
          title="Reject declaration"
          message={
            <div className="stack" style={{ gap: 8 }}>
              <p style={{ margin: 0 }}>
                {rejecting.submitterCompanyName
                  ? // Story 6.10: a manager submission is a declaration the Vendor never made —
                    // emailing them would confuse them and leak the customer's reason text, so
                    // no vendor notice is sent (the rejection itself is still audited).
                    `The submission stays pending. The reason is recorded in the audit trail. ${rejecting.submitterCompanyName} submitted this on the vendor's behalf, so no email is sent to the vendor.`
                  : 'The declaration stays pending. The reason is recorded and emailed to the vendor so they can correct and re-declare.'}
              </p>
              <label className="stack" style={{ gap: 4 }}>
                <span className="field-label">Reason</span>
                <textarea
                  aria-label="Rejection reason"
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);
                    if (rejectError) setRejectError(null);
                  }}
                  rows={3}
                  maxLength={MAX_REASON_LEN}
                  style={{
                    padding: '8px 10px',
                    background: 'var(--surface-2)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--rounded-md)',
                    color: 'var(--text)',
                    fontFamily: 'var(--font-sans)',
                    fontSize: 14,
                    resize: 'vertical',
                  }}
                />
              </label>
            </div>
          }
          confirmLabel="Reject"
          busy={rejectBusy}
          error={rejectError}
          onConfirm={() => void onConfirmReject()}
          onCancel={() => setRejecting(null)}
        />
      )}
    </>
  );
}
